import { LitElement, html, TemplateResult, nothing } from 'lit';
import { property, customElement, query, queryAsync } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { FontSizes } from '../../../../types/common/types';
import { translateFn } from '../../../../utils/translator.helper';
import { BaseWebComponentTranslator } from '../../../../framework/BaseWebComponentTranslator';
import { MisconfigurationType, MitigationType, RiskTypes, State } from '../../../common/constants';
import {
  handleWatchlistChangeButtonClick,
  overlayOpenListener,
  renderButtonGroup,
  renderRegions,
  renderSideMenuCompanyIfNeeded,
  renderSideMenuCompliance,
  renderSideMenuMitigationType,
  renderSideMenuOperatingSystem,
  renderSideMenuRiskScore,
  renderSideMenuSectionTitle,
  renderSideMenuState,
  renderSideMenuStatus,
  renderSideMenuWatchlist,
  RiskManagementSideMenuEvents,
  SideMenuButton,
} from '../../common/common';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { GzButtonWebComponent } from '../../../../ui/gzButton';
import { getWatchlistButtonTextBasedOnState } from '../../../common/utils';
import { ContextMenuOverlayElement } from '../../../../ui/gzContextMenuOverlay';
import { DeviceExtraDetails, InitialDropdownDetails } from '../../components/deviceDetails/types/types';
import { DeviceDetailsWebComponent } from '../../components/deviceDetails';
import { DeviceExtraDetailsWebComponent } from '../../components/deviceExtraDetails';

/**
 * @summary Type here the description of this component
 * @tag misconfigurations-side-menu
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('misconfigurations-side-menu')
export class MisconfigurationsSideMenuWebComponent extends LitElement {
  @property({ type: Object, attribute: false })
  item = {} as any;

  @property({ type: Object, attribute: false })
  complianceDetails = null as null | any;

  @property({ type: Object, attribute: false })
  deviceInitialDropdownDetails = null as null | InitialDropdownDetails;

  @property({ type: Object, attribute: false })
  initialDeviceDetails = null as null | any;

  @property({ type: Object, attribute: false })
  deviceDetails = null as null | any;

  @property({ type: Boolean, attribute: false })
  deviceDetailsHasErrors = false;

  @property({ type: Boolean, attribute: false })
  deviceDetailsEmptyState = false;

  @query('#watchlist-button')
  private readonly watchlistButton!: GzButtonWebComponent;

  @queryAsync('#extra-details-context-menu-side-panel')
  private readonly overlay!: ContextMenuOverlayElement;

  @query('#device-details-component')
  public readonly deviceDetailsComponent!: DeviceDetailsWebComponent;

  @property({ type: Object, attribute: false })
  deviceExtraDetailsInitialDropdown = null as null | InitialDropdownDetails;

  @property({ type: Object, attribute: false })
  deviceExtraDetails = null as null | DeviceExtraDetails;

  @query('#device-extra-details-component')
  public readonly deviceExtraDetailsComponent!: DeviceExtraDetailsWebComponent;

  @property({ type: Boolean, attribute: true, reflect: true })
  showExtraDetailsPanel = false;

  riskType = RiskTypes.MISCONFIGURATIONS;

  static get styles() {
    return styles;
  }

  render(): TemplateResult {
    if (this.showExtraDetailsPanel) {
      return html`${this.renderExtraDetailsPanel()}`;
    }

    return html`
      <div class="header mt-6">
        <gz-title fontSize="${FontSizes.HEADING_5}" class="mb-2">
          ${Application.modules.riskManagement.controller.helpers.routinesTranslations.translateRoutineName(
            this.item.indicatorId
          )}
        </gz-title>
      </div>
      <div class="content">
        ${this.renderGeneralSection()} ${this.renderDetailsSection()} ${this.renderMitigationDetailsSection()}
        ${this.renderDeviceDetails()}
      </div>
      <gz-context-menu-overlay id="extra-details-context-menu-side-panel" @openContextMenu="${overlayOpenListener}">
      </gz-context-menu-overlay>
    `;
  }

  private renderExtraDetailsPanel() {
    return html`
      <div class="header mt-2">
        <gz-button
          class="backBtn"
          type="quiet"
          color="secondary"
          buttonheight="small"
          buttonfontsize="small"
          @click="${this.handleBackButtonClick}"
        >
          <gz-icon class="backBtn-icon" color="secondary" size="24" name="ds-icon-arrow_left_collapse"></gz-icon>
          ${translateFn('Back')}
        </gz-button>
        <gz-title fontSize="${FontSizes.HEADING_5}" class="mb-2 mt-3">
          ${Application.modules.riskManagement.controller.helpers.routinesTranslations.translateRoutineName(
            this.item.indicatorId
          )}
        </gz-title>
      </div>
      <device-extra-details
        id="device-extra-details-component"
        .initialDropdownDetails="${this.deviceExtraDetailsInitialDropdown}"
        .itemsType="${this.deviceExtraDetails?.itemsType}"
        .items="${this.deviceExtraDetails?.items}"
        ?showDeviceDropdown="${true}"
      >
      </device-extra-details>
    `;
  }

  public changeWatchlistButtonState(isLoading: boolean) {
    this.watchlistButton.setLoadingState(isLoading);
  }

  private renderGeneralSection(): TemplateResult {
    return html`
      <div class="section pb-2" data-section="general">
        ${renderSideMenuSectionTitle(translateFn('General'))} ${this.renderMisconfigurationType(this.item.subcategory)}
        ${renderSideMenuRiskScore(this.item, this.overlay, this.riskType)} ${renderSideMenuStatus(this.item.status)}
        ${renderSideMenuState(this.item.state)} ${renderSideMenuOperatingSystem(this.item.operatingSystem)}
        ${renderSideMenuMitigationType(this.item.mitigation)} ${renderSideMenuCompanyIfNeeded(this.item.company?.name)}
        ${renderRegions(this.item.regions)} ${renderSideMenuCompliance(this.item.compliance)}
        ${renderSideMenuWatchlist(this.item.watchlist)}
        ${renderButtonGroup([
          {
            text: translateFn('View resources'),
            handler: this.handleViewDevicesButtonClick,
            id: 'view-devices-button',
          },
          {
            text: getWatchlistButtonTextBasedOnState(this.item.watchlist),
            handler: handleWatchlistChangeButtonClick.bind(this),
            id: 'watchlist-button',
          },
        ])}
      </div>
    `;
  }

  private renderMisconfigurationType(misconfigurationType: MisconfigurationType) {
    return html`
      <gz-labeled-content label="${translateFn('Finding type')}">
        ${BaseWebComponentTranslator.getInstance().getMisconfigurationType(misconfigurationType)}
      </gz-labeled-content>
    `;
  }

  private renderDetailsSection(): TemplateResult {
    return html`
      <div class="section pb-2 pt-1" data-section="details">
        ${renderSideMenuSectionTitle(translateFn('Details'))}
        <gz-text class="text-description" fontSize="${FontSizes.PARAGRAPH_12}">
          ${unsafeHTML(
            Application.modules.riskManagement.controller.helpers.routinesTranslations.translateRoutineDescription(
              this.item.indicatorId
            )
          )}
        </gz-text>
        ${this.renderComplianceStandards()}
      </div>
    `;
  }

  private renderComplianceStandards() {
    if (this.item.compliance.length === 0) {
      return nothing;
    }

    const dialogContainer = document.querySelector('gz-misconfigurations-app')?.parentElement as HTMLElement;

    return html`
      <compliance-standards
        .indicatorId="${this.item.indicatorId}"
        .dialogContainer="${dialogContainer}"
      ></compliance-standards>
    `;
  }

  private renderMitigationDetailsSection(): TemplateResult {
    const { disabled, tooltip } = this.getFixRiskButtonDisableState();
    const { disabledRollback, tooltipRollback } = this.getRollbackRiskButtonDisableState();

    const buttons: Array<SideMenuButton> = [
      {
        text: translateFn('Fix risk'),
        handler: this.handleFixRiskButtonClick,
        disabled,
        tooltiptext: tooltip,
        id: 'fix-risk-button',
      },
      {
        text: translateFn('Roll back fix'),
        handler: this.handleRollbackFixButtonClick,
        disabled: disabledRollback,
        tooltiptext: tooltipRollback,
        id: 'rollback-fix-button',
      },
    ];

    if (this.item.state === State.IGNORED) {
      buttons.push({
        text: translateFn('Restore ignored risk'),
        handler: this.handleRestoreButtonClick,
        id: 'restore-risk-button',
        disabled: !this.item.company.specifics.features.iorUsable,
        tooltiptext: translateFn('Your current license does not provide access to this feature.'),
      });
    } else {
      buttons.push({
        text: translateFn('Ignore risk'),
        handler: this.handleIgnoreButtonClick,
        id: 'ignore-risk-button',
        disabled: !this.item.company.specifics.features.iorUsable,
        tooltiptext: translateFn('Your current license does not provide access to this feature.'),
      });
    }

    return html`
      <div class="section pb-2 pt-1" data-section="mitigation">
        ${renderSideMenuSectionTitle(translateFn('Risk mitigation'))}
        <gz-text class="pb-1 text-description" fontSize="${FontSizes.PARAGRAPH_12}">
          ${unsafeHTML(
            Application.modules.riskManagement.controller.helpers.routinesTranslations.translateRoutineAction(
              this.item.indicatorId
            )
          )}
        </gz-text>
        ${renderButtonGroup(buttons)}
      </div>
    `;
  }

  private renderDeviceDetails(): any {
    if (this.deviceDetailsEmptyState) {
      return nothing;
    }

    return html`
      <div class="section pb-2 pt-1" data-section="deviceDetails">
        <device-details
          id="device-details-component"
          .detailsTitle="${translateFn('Resources')}"
          .deviceInformation="${this.deviceDetails}"
          .initialDropdownDetails="${this.deviceInitialDropdownDetails}"
          ?showDeviceDropdown="${true}"
          ?hasErrors="${this.deviceDetailsHasErrors}"
          ?emptyState="${this.deviceDetailsEmptyState}"
        ></device-details>
      </div>
    `;
  }

  handleViewDevicesButtonClick(): void {
    this.dispatchEvent(
      new CustomEvent(RiskManagementSideMenuEvents.VIEW_DEVICES_BY_MISCONFIGURATION, {
        detail: {
          indicatorId: this.item.indicatorId,
          companyId: this.item.companyId,
        },
        bubbles: true,
        cancelable: true,
        composed: false,
      })
    );
  }

  private handleIgnoreButtonClick(): void {
    this.dispatchEvent(
      new CustomEvent(RiskManagementSideMenuEvents.IGNORE_MISCONFIGURATIONS, {
        detail: {
          indicatorsIds: [this.item.indicatorId],
          companyId: this.item.company._id,
        },
        bubbles: true,
        cancelable: true,
        composed: false,
      })
    );
  }

  private handleRestoreButtonClick(): void {
    this.dispatchEvent(
      new CustomEvent(RiskManagementSideMenuEvents.RESTORE_MISCONFIGURATIONS, {
        detail: {
          indicatorsIds: [this.item.indicatorId],
          companyId: this.item.company._id,
        },
        bubbles: true,
        cancelable: true,
        composed: false,
      })
    );
  }

  private handleFixRiskButtonClick(): void {
    this.dispatchEvent(
      new CustomEvent(RiskManagementSideMenuEvents.FIX_MISCONFIGURATIONS, {
        detail: {
          indicatorsIds: [this.item.indicatorId],
          companyId: this.item.company._id,
          hasFrat: this.item.hasFrat,
        },
        bubbles: true,
        cancelable: true,
        composed: false,
      })
    );
  }

  private handleRollbackFixButtonClick(): void {
    this.dispatchEvent(
      new CustomEvent(RiskManagementSideMenuEvents.ROLLBACK_MISCONFIGURATIONS, {
        detail: {
          indicatorsIds: [this.item.indicatorId],
          companyId: this.item.company._id,
          hasFrat: this.item.hasFrat,
        },
        bubbles: true,
        cancelable: true,
        composed: false,
      })
    );
  }

  private getFixRiskButtonDisableState() {
    if (this.item.mitigation === MitigationType.MANUAL) {
      return {
        disabled: true,
        tooltip: translateFn('Automatic mitigation is not applicable for this risk.'),
      };
    }

    if (!this.item.company.specifics.features.iorUsable) {
      return {
        disabled: true,
        tooltip: translateFn('Your current license does not provide access to this feature.'),
      };
    }

    const affectedDevicesCount = this.item.affectedDevices.reduce((acc, group) => {
      return acc + group.ids.length;
    }, 0);
    if (affectedDevicesCount === 0) {
      return {
        disabled: true,
        tooltip: translateFn('The fix is not applicable to any resources.'),
      };
    }

    return {
      disabled: false,
      tooltip: null,
    };
  }

  private getRollbackRiskButtonDisableState() {
    if (this.item.mitigation === MitigationType.MANUAL) {
      return {
        disabledRollback: true,
        tooltipRollback: translateFn('Automatic mitigation is not applicable for this risk.'),
      };
    }

    if (!this.item.company.specifics.features.iorUsable) {
      return {
        disabledRollback: true,
        tooltipRollback: translateFn('Your current license does not provide access to this feature.'),
      };
    }

    if (this.item.countDevicesToRollback === 0) {
      return {
        disabledRollback: true,
        tooltipRollback: translateFn('Rolling back is not applicable to any endpoints.'),
      };
    }

    return {
      disabledRollback: false,
      tooltipRollback: null,
    };
  }

  private handleBackButtonClick() {
    this.showExtraDetailsPanel = false;
    this.deviceDetails = this.initialDeviceDetails;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'misconfigurations-side-menu': MisconfigurationsSideMenuWebComponent;
  }
}
